/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fetcher } from "./fetcher";
import { queryKeys } from "./queryKeys";

export const useProductsTypeAttribute = ({ search = "", orderField = "", orderDirection = "", serviceId = null, onPage = 10, enabled = true } = {}) => {
  return useInfiniteQuery({
    queryKey: ["products_type_attribute", { search, orderField, orderDirection, serviceId, onPage }],
    enabled,
    queryFn: ({ pageParam }) => {
      let url = "/public/products/?filter[type]=attribute";
      if (search !== "") url += `&filter[search]=${encodeURIComponent(search)}`;
      if (serviceId) url += `&filter[service_id]=${serviceId}`;
      return fetcher({
        method: "get",
        url,
        params: { page: pageParam, on_page: onPage, order_field: orderField, order_direction: orderDirection },
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
        lastPage?.data?.length === onPage ? allPages.length + 1 : undefined,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useProductsTypeProduct = ({ search = "", orderField = "", orderDirection = "", categoryId = null, serviceId = null, standaloneOnly = false, onPage = 10, enabled = true } = {}) => {
  return useInfiniteQuery({
    queryKey: queryKeys.products.list({ search, orderField, orderDirection, categoryId, serviceId, standaloneOnly, onPage }),
    enabled,
    queryFn: ({ pageParam }) => {
      let url = "/public/products/?filter[type]=product";
      if (search !== "") url += `&filter[search]=${encodeURIComponent(search)}`;
      if (categoryId) url += `&filter[category_id]=${categoryId}`;
      if (serviceId) url += `&filter[service_id]=${serviceId}`;
      if (standaloneOnly) url += "&filter[standalone_only]=1";
      return fetcher({
        method: "get",
        url,
        params: { page: pageParam, on_page: onPage, order_field: orderField, order_direction: orderDirection },
      }).then(res => res.data);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.length === onPage ? allPages.length + 1 : undefined,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useProductsCategories = ({ enabled = true } = {}) => {
  return useQuery({
    queryKey: queryKeys.products.categories,
    queryFn:  () => fetcher({ method: "get", url: "/public/products/categories" }).then(res => res?.data ?? res),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};